// pages/TarifaSupervision/form/AvisoFields.jsx
import React from "react";
import { useFormikContext, Form } from "formik";
import {
  Box,
  Divider,
  Chip,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Button,
  LinearProgress,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { CustomDatePicker } from "../../../styles/styles";
import SoporteDropzone from "../components/SoporteDropzone";

/**
 * Este componente asume que está renderizado DENTRO de un <Formik> y de un <Form>.
 * Usa useFormikContext() para acceder a values, setFieldValue, touched, errors, etc.
 *
 * Props mínimas que le pasamos desde el padre:
 * - colors, theme
 * - tipoServicio, availableYears, availableMonths
 * - validateMonthYear, calculateMontoPagarYMora
 * - mostrarAporte, setMostrarAporte, isAporteDisabled
 * - isProcessing, isGuardarDisabled
 * - onOpenDocs -> handler para abrir el modal de documentación
 * - notify(severity, message) -> para mostrar snackbar
 */
export default function AvisoFields({
  colors,
  theme,
  tipoServicio,
  availableYears,
  availableMonths,
  validateMonthYear,
  calculateMontoPagarYMora,
  mostrarAporte,
  setMostrarAporte,
  isAporteDisabled,
  isProcessing,
  isGuardarDisabled,
  listaAvisos,
  onOpenDocs,
  notify,
}) {
  const {
    values,
    setFieldValue,
    handleBlur,
    errors,
    touched,
    handleSubmit: formikSubmit,
  } = useFormikContext();

  // Gates para habilitar por pasos
  const servicioOK = Boolean(values.tipoServicio);
  const anioOK = servicioOK && Boolean(values.anio);
  const mesOK = anioOK && Boolean(values.mes);
  const fechaPagoOK = mesOK && Boolean(values.fechaPago);

  return (
    <Form onSubmit={formikSubmit}>
      <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gridTemplateRows="auto" gap="20px">
        <Box gridColumn={"span 12"}>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", sm: "repeat(4, 1fr)", lg: "repeat(4, 1fr)" }}
            gap="20px"
          >
            {/* Título + Chip de documentación */}
            <Box id="tour-doc-status" sx={{ gridColumn: { xs: "span 4", sm: "span 4", md: "span 4" } }}>
            <Divider
              sx={{
                display: "flex",
                alignItems: "center",
                "&::before, &::after": {
                  borderColor:
                    listaAvisos && listaAvisos.length > 0
                      ? colors.redAccent[500]
                      : colors.gov[500],
                },
              }}
              textAlign="left"
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                Datos para generación de aviso de cobro
              </Box>

              <Chip
                label={
                  listaAvisos && listaAvisos.length > 0
                    ? "Documentación Incompleta"
                    : "Documentación Completa"
                }
                color={listaAvisos && listaAvisos.length > 0 ? "error" : "success"}
                size="small"
                sx={{
                  ml: 2,
                  backgroundColor:
                    listaAvisos && listaAvisos.length > 0
                      ? colors.redAccent[500]
                      : colors.greenAccent[500],
                  color: colors.black[950],
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor:
                      listaAvisos && listaAvisos.length > 0
                        ? colors.redAccent[500]
                        : colors.greenAccent[500],
                  },
                }}
                onClick={onOpenDocs}
              />
            </Divider>
            </Box>

            {/* Switch de Aporte */}
            <Box id="tour-aporte-switch" gridColumn="span 4">
              <Box display="flex" alignItems="center" gap={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={mostrarAporte}
                      onChange={(e) => setMostrarAporte(e.target.checked)}
                      color="secondary"
                      disabled={isAporteDisabled}
                    />
                  }
                  label="¿Desea agregar los conceptos para el Aporte Mensual?"
                />
                {isAporteDisabled && (
                  <Typography
                    variant="body2"
                    sx={{ color: "warning.main", fontStyle: "italic", whiteSpace: "nowrap" }}
                  >
                    Tiene pendiente generar avisos del aporte mensual.
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Fecha Emisión (solo lectura) */}
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <CustomDatePicker
                disabled
                label="Fecha Emisión Aviso"
                value={values.fechaEmision}
                onChange={(value) => setFieldValue("fechaEmision", value, true)}
                components={{ OpenPickerIcon: CalendarTodayIcon }}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    id: "tour-fecha-emision",
                    InputProps: { readOnly: true },
                    fullWidth: true,
                    variant: "filled",
                    name: "fechaEmision",
                    onBlur: handleBlur,
                    error: touched.fechaEmision && Boolean(errors.fechaEmision),
                    helperText: touched.fechaEmision && errors.fechaEmision,
                    InputLabelProps: { style: { color: colors.whiteAccent[950] }, shrink: true },
                    sx: {
                      gridColumn: { xs: "span 4", sm: "span 2", md: "span 2" },
                      "& .MuiInputBase-input": { color: colors.whiteAccent[950] },
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: colors.whiteAccent[950],
                      },
                      "& .MuiInputLabel-root.Mui-disabled": { color: colors.whiteAccent[950] },
                    },
                    disabled: true,
                  },
                }}
                format="DD/MMMM/YYYY"
              />
            </LocalizationProvider>

            {/* Servicio */}
            <Autocomplete
              id="tour-servicio"
              sx={{ gridColumn: { xs: "span 4", sm: "span 2", md: "span 2" } }}
              fullWidth
              color="secondary"
              options={tipoServicio}
              getOptionLabel={(option) => option.label}
              onChange={(event, newValue) => {
                setFieldValue("tipoServicio", newValue ? newValue.value : "", true);
                // Limpiar dependientes
                setFieldValue("mes", "", false);
                setFieldValue("fechaPago", null, false);
                setFieldValue("ingresoServicio", "", false);
                setFieldValue("montoPagar", 0, false);
                setFieldValue("mora", 0, false);
                setFieldValue("montoAporte", 0, false);
                setFieldValue("moraAporte", 0, false);
                setFieldValue("fechaVencimiento", null, false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  color="secondary"
                  label="Servicio"
                  variant="filled"
                  name="tipoServicio"
                  onBlur={handleBlur}
                  error={touched.tipoServicio && Boolean(errors.tipoServicio)}
                  helperText={touched.tipoServicio && errors.tipoServicio}
                />
              )}
            />

            {/* Año */}
            <Autocomplete
              id="tour-anio"
              sx={{ gridColumn: { xs: "span 4", sm: "span 1", md: "span 1" } }}
              fullWidth
              color="secondary"
              disabled={!servicioOK}
              options={availableYears}
              getOptionLabel={(option) => option.label}
              onChange={(event, newValue) => {
                setFieldValue("anio", newValue ? newValue.value : "", true);
                // Limpiar dependientes
                setFieldValue("mes", "", false);
                setFieldValue("fechaPago", null, false);
                setFieldValue("ingresoServicio", "", false);
                setFieldValue("montoPagar", 0, false);
                setFieldValue("mora", 0, false);
                setFieldValue("montoAporte", 0, false);
                setFieldValue("moraAporte", 0, false);
                setFieldValue("fechaVencimiento", null, false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  color="secondary"
                  label="Año"
                  variant="filled"
                  name="anio"
                  onBlur={handleBlur}
                  error={touched.anio && Boolean(errors.anio)}
                  helperText={touched.anio && errors.anio}
                />
              )}
            />

            {/* Mes */}
            <Autocomplete
              id="tour-mes"
              sx={{ gridColumn: { xs: "span 4", sm: "span 1", md: "span 1" } }}
              fullWidth
              color="secondary"
              disabled={!anioOK}
              options={availableMonths}
              getOptionLabel={(option) => option.label}
              onChange={async (event, newValue) => {
                const mesValue = newValue ? newValue.value : "";
                setFieldValue("mes", mesValue, true);
                // Validación de mes/año/servicio
                const errorMessage = await validateMonthYear(mesValue, values.anio, values.tipoServicio);
                if (errorMessage) {
                  notify("error", errorMessage);
                } else {
                  const parsedValue = parseFloat(values.ingresoServicio);
                  const ingresoServicio = Number.isNaN(parsedValue) || parsedValue < 0 ? 0 : parsedValue;

                  const { montoPagar, mora, fechaVencimiento, moraAporte, montoAporte } =
                    calculateMontoPagarYMora(
                      ingresoServicio,
                      dayjs(values.fechaPago),
                      mesValue || values.mes,
                      values.anio
                    );

                  setFieldValue("montoPagar", montoPagar || 0, false);
                  setFieldValue("mora", mora || 0, false);
                  setFieldValue("fechaVencimiento", dayjs(fechaVencimiento), false);
                  setFieldValue("moraAporte", moraAporte || 0, false);
                  setFieldValue("montoAporte", montoAporte || 0, false);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  color="secondary"
                  label="Mes"
                  variant="filled"
                  name="mes"
                  onBlur={handleBlur}
                  error={touched.mes && Boolean(errors.mes)}
                  helperText={touched.mes && errors.mes}
                />
              )}
            />

            {/* Fecha vencimiento (siempre readonly) */}
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <DatePicker
                label="Fecha vencimiento de la obligación"
                color="secondary"
                value={values.fechaVencimiento}
                onChange={() => {}}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    id: "tour-fecha-vencimiento",
                    color: "secondary",
                    InputProps: { readOnly: true },
                    fullWidth: true,
                    variant: "filled",
                    name: "fechaVencimiento",
                    onBlur: handleBlur,
                    error: touched.fechaVencimiento && Boolean(errors.fechaVencimiento),
                    helperText: touched.fechaVencimiento && errors.fechaVencimiento,
                    sx: { gridColumn: { xs: "span 4", sm: "span 2", md: "span 2" } },
                  },
                }}
                readOnly
                minDate={dayjs()}
                format="DD/MMMM/YYYY"
              />
            </LocalizationProvider>

            {/* Fecha de pago (depende de Mes) */}
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <DatePicker
                label="Fecha de pago del aviso"
                disabled={!mesOK}
                value={values.fechaPago}
                onChange={(value) => {
                  setFieldValue("fechaPago", value, true);
                  if (value) {
                    const { montoPagar, mora, moraAporte, montoAporte } = calculateMontoPagarYMora(
                      values.ingresoServicio,
                      value,
                      values.mes,
                      values.anio
                    );
                    setFieldValue("montoPagar", parseFloat(montoPagar) || 0, false);
                    setFieldValue("mora", mora || 0, false);
                    setFieldValue("moraAporte", moraAporte || 0, false);
                    setFieldValue("montoAporte", montoAporte || 0, false);
                  }
                }}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    id: "tour-fecha-pago",
                    color: "secondary",
                    InputProps: { readOnly: true },
                    fullWidth: true,
                    variant: "filled",
                    name: "fechaPago",
                    onBlur: handleBlur,
                    error: touched.fechaPago && Boolean(errors.fechaPago),
                    helperText: touched.fechaPago && errors.fechaPago,
                    sx: { gridColumn: { xs: "span 4", sm: "span 2", md: "span 2" } },
                  },
                }}
                minDate={dayjs().startOf("day")}
                format="DD/MMMM/YYYY"
              />
            </LocalizationProvider>

            {/* Ingreso del servicio (depende de que ya tenga Fecha de pago) */}
            <TextField
              id="tour-ingreso-servicio"
              sx={{ gridColumn: { xs: "span 4", sm: "span 2", md: "span 2" } }}
              fullWidth
              color="secondary"
              label="Ingreso del servicio"
              name="ingresoServicio"
              variant="filled"
              disabled={!fechaPagoOK}
              value={values.ingresoServicio}
              onChange={(e) => {
                const cleanedValue = e.target.value.replace(/,/g, "");
                setFieldValue("ingresoServicio", cleanedValue, false);
                if (!isNaN(cleanedValue)) {
                  const { montoPagar, mora, moraAporte, montoAporte } = calculateMontoPagarYMora(
                    cleanedValue,
                    values.fechaPago,
                    values.mes,
                    values.anio
                  );
                  setFieldValue("montoPagar", parseFloat(montoPagar) || 0, false);
                  setFieldValue("mora", mora || 0, false);
                  setFieldValue("moraAporte", moraAporte || 0, false);
                  setFieldValue("montoAporte", montoAporte || 0, false);
                }
              }}
              onBlur={handleBlur}
              error={touched.ingresoServicio && Boolean(errors.ingresoServicio)}
              helperText={touched.ingresoServicio && errors.ingresoServicio}
            />

            {/* Campos calculados / readonly */}
            <Box
              id="tour-montos"
              sx={{
                gridColumn: { xs: "span 4", sm: "span 4", md: "span 4" },
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: "20px",
              }}
            >
              <TextField
                disabled
                fullWidth
                color="secondary"
                label="Monto tarifa supervisión"
                name="montoPagar"
                variant="filled"
                value={values.montoPagar ?? 0}
                onBlur={handleBlur}
                error={touched.montoPagar && Boolean(errors.montoPagar)}
                helperText={touched.montoPagar && errors.montoPagar}
                sx={{
                  "& .MuiInputBase-input": { color: colors.whiteAccent[950] },
                  "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: colors.whiteAccent[950] },
                  "& .MuiInputLabel-root.Mui-disabled": { color: colors.whiteAccent[950] },
                }}
              />

              <TextField
                disabled
                fullWidth
                color="secondary"
                label="Mora tarifa por supervisión"
                name="mora"
                variant="filled"
                value={values.mora ?? 0}
                onBlur={handleBlur}
                error={touched.mora && Boolean(errors.mora)}
                helperText={touched.mora && errors.mora}
                sx={{
                  "& .MuiInputBase-input": { color: colors.whiteAccent[950] },
                  "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: colors.whiteAccent[950] },
                  "& .MuiInputLabel-root.Mui-disabled": { color: colors.whiteAccent[950] },
                }}
              />
            </Box>

            {mostrarAporte && (
              <>
                <TextField
                  disabled
                  fullWidth
                  color="secondary"
                  label="Monto Aporte Mensual"
                  name="montoAporte%"
                  variant="filled"
                  value={values.montoAporte ?? 0}
                  onBlur={handleBlur}
                  error={touched.montoAporte && Boolean(errors.montoAporte)}
                  helperText={touched.montoAporte && errors.montoAporte}
                  sx={{
                    gridColumn: { xs: "span 4", sm: "span 2", md: "span 2" },
                    "& .MuiInputBase-input": { color: colors.whiteAccent[950] },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: colors.whiteAccent[950],
                    },
                    "& .MuiInputLabel-root.Mui-disabled": { color: colors.whiteAccent[950] },
                  }}
                />
                <TextField
                  disabled
                  fullWidth
                  color="secondary"
                  label="Mora Aporte Mensual"
                  name="moraAporte%"
                  variant="filled"
                  value={values.moraAporte ?? 0}
                  onBlur={handleBlur}
                  error={touched.moraAporte && Boolean(errors.moraAporte)}
                  helperText={touched.moraAporte && errors.moraAporte}
                  sx={{
                    gridColumn: { xs: "span 4", sm: "span 2", md: "span 2" },
                    "& .MuiInputBase-input": { color: colors.whiteAccent[950] },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: colors.whiteAccent[950],
                    },
                    "& .MuiInputLabel-root.Mui-disabled": { color: colors.whiteAccent[950] },
                  }}
                />
              </>
            )}

            {/* Dropzone */}
            <Box id="tour-dropzone" sx={{ gridColumn: { xs: "span 4", sm: "span 4", md: "span 2" } }}>
              <SoporteDropzone
                values={values}
                setFieldValue={setFieldValue}
                error={touched.detalleIngreso && errors.detalleIngreso}
                notify={notify}
                palette={colors}
                borderColor={
                  touched.detalleIngreso && errors.detalleIngreso
                    ? theme.palette.error.main
                    : theme.palette.secondary.main
                }
              />
            </Box>

            {/* Descripción */}
            <TextField
              id="tour-descripcion"
              fullWidth
              color="secondary"
              multiline
              minRows={4}
              label="Descripción"
              name="descripcion"
              variant="filled"
              value={values.descripcion}
              onChange={(e) => setFieldValue("descripcion", e.target.value, true)}
              onBlur={handleBlur}
              error={touched.descripcion && Boolean(errors.descripcion)}
              helperText={touched.descripcion && errors.descripcion}
              sx={{ gridColumn: { xs: "span 4", sm: "span 4", md: "span 2" } }}
            />

            {/* Submit + loader */}
            <Box id="tour-submit" sx={{ gridColumn: "span 4" }}>
              {isProcessing && (
                <LinearProgress
                  sx={{
                    backgroundColor: colors.gov[500],
                    "& .MuiLinearProgress-bar": { backgroundColor: colors.pacificBlue[500] },
                    mt: 2,
                    mb: 2,
                  }}
                />
              )}
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="secondary"
                disabled={isGuardarDisabled || !!(listaAvisos && listaAvisos.length > 0)}
              >
                Generar Aviso de cobro
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Form>
  );
}
